import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdDownload, MdSettings, MdDragIndicator } from 'react-icons/md';
import { PiSortDescendingLight, PiSortAscendingLight } from 'react-icons/pi';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { authService } from '../../../utils/authService';
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import * as XLSX from 'xlsx';
import { 
  ColumnConfig, 
  getDefaultSubBatchDetailsColumns, 
  loadColumnVisibility, 
  updateColumnVisibility, 
  resetColumnVisibility,
  PAGE_KEYS 
} from '../../../utils/columnVisibilityUtils';
import { compareTimeliness, isTimelinessSortField } from '../../../utils/timelinessSortUtils';
import { compareNumericScore, compareMaybeNumeric, isScoreSortField } from '../../../utils/numericSortUtils';

interface Project {
  name: string;
  id: number;
  batch_project_id: number;
}

interface FilterData {
  years: number[];
  colleges: Array<{ name: string; id: string }>;
  courses: Array<{ name: string; id: string }>;
  batches: Array<{ name: string; id: string }>;
  branches: Array<{ name: string; id: string }>;
  subjects: Array<{ name: string; id: string }>;
  projects: Project[];
  coordinators: Array<{ name: string; id: string }>;
  relations: {
    college_courses: { [key: string]: string[] };
    course_batches: { [key: string]: string[] };
    batch_subjects: { [key: string]: string[] };
    batch_projects: { [key: string]: number[] };
    college_branches: { [key: string]: string[] };
    coordinators_relations: { [key: string]: string[] };
  };
}

interface StudentData {
  ID: string;
  USN: string;
  Name: string;
  Batch: string;
  Progress: string;
  Time_Spent: string;
  no_of_hours_should_be_completed_by_current_day?: string | null;
  performance_percentage?: number | null;
  progress_percentage_of_should_be_completed_subtasks?: number | null;
  Score_WT: string;
  Live_Sessions: string;
  Score_FT: string;
  Grade: string;
  Score_wts: {
    [subject: string]: {
      [week: string]: string;
    };
  };
  Score_fts: {
    [subject: string]: {
      [week: string]: string;
    };
  };
  batch_id: string;
}

interface ReportData {
  students: StudentData[];
  total_count: number;
}

const SubBatchDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const savedBatchFilters = sessionStorage.getItem('batchDetailsFilters');
  const savedBatchFiltersParsed = savedBatchFilters ? JSON.parse(savedBatchFilters) : {};
  const navigationPayload = location.state?.payload || {};
  const navigationBatchId = location.state?.batchId;
  const navigationBatchName = location.state?.batchName;
  const navigationCoordinatorId = location.state?.coordinatorId;
  const navigationCoordinatorName = location.state?.coordinatorName;
  const previousFilters = location.state?.payload || location.state?.filters || savedBatchFiltersParsed || {};
  
  useEffect(() => {
    if (!sessionStorage.getItem("Coordinator")) {
      navigate('/');
      return;
    }
  }, [navigate]);


  const [filters, setFilters] = useState<FilterData>({
    years: [],
    colleges: [],
    courses: [],
    batches: [],
    branches: [],
    subjects: [],
    projects: [],
    coordinators: [],
    relations: {
      college_courses: {},
      course_batches: {},
      batch_subjects: {},
      batch_projects: {},
      college_branches: {},
      coordinators_relations: {}
    }
  });
  const [report, setReport] = useState<ReportData>({ students: [], total_count: 0 });
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [dataError, setDataError] = useState<string>('');
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const [columns, setColumns] = useState<ColumnConfig[]>(() => 
    loadColumnVisibility(PAGE_KEYS.SUB_BATCH_DETAILS, getDefaultSubBatchDetailsColumns())
  );
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [totalTimeToBeSpent, setTotalTimeToBeSpent] = useState<string>('');
  
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedCollege, setSelectedCollege] = useState<string>('');
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');

  const [availableCourses, setAvailableCourses] = useState<Array<{ name: string; id: string }>>([]);
  const [availableBatches, setAvailableBatches] = useState<Array<{ name: string; id: string }>>([]);
  const [availableSubjects, setAvailableSubjects] = useState<Array<{ name: string; id: string }>>([]);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [availableCoordinators, setAvailableCoordinators] = useState<Array<{ name: string; id: string }>>([]);

  const [selectedCoordinator, setSelectedCoordinator] = useState<string>('');
  const [isScoreWTExpanded, setIsScoreWTExpanded] = useState<boolean>(false);
  const [expandedWTColumns, setExpandedWTColumns] = useState<ColumnConfig[]>([]);
  const [isScoreFTExpanded, setIsScoreFTExpanded] = useState<boolean>(false);
  const [expandedFTColumns, setExpandedFTColumns] = useState<ColumnConfig[]>([]);
  const [hasAutoApplied, setHasAutoApplied] = useState<boolean>(false);
  const [shouldAutoApply, setShouldAutoApply] = useState<boolean>(false);

  useEffect(() => {
    const loadFilters = async () => {
      setLoading(true);
      setError('');
      try {
        const userId = sessionStorage.getItem('Id');
        if (!userId) {
          setError('User ID not found. Please login again.');
          return;
        }
        
        const response = await authService.get(`batch-comparison/filters/${userId}`);
        setFilters(response.data);

        const year = navigationPayload.year || previousFilters.year;
        const collegeId = navigationPayload.college_id || previousFilters.college_id || '';
        const courseId = navigationPayload.course_id || previousFilters.course_id || '';
        const batchId = navigationBatchId || navigationPayload.batch_id || previousFilters.batch_id || '';
        const coordinatorId = navigationPayload.coordinator_id || navigationCoordinatorId || previousFilters.coordinator_id || '';
        
        if (year && response.data.years.includes(parseInt(year))) {
          setSelectedYear(year.toString());
        }
        
        if (collegeId && response.data.colleges.find((c: { id: string }) => c.id === collegeId)) {
          setSelectedCollege(collegeId);
          const courses = response.data.courses.filter((course: { name: string; id: string }) => 
            response.data.relations.college_courses[collegeId]?.includes(course.id)
          );
          setAvailableCourses(courses);
          
          if (courseId && courses.find((c: { id: string }) => c.id === courseId)) {
            setSelectedCourse(courseId);
            const batches = response.data.batches.filter((batch: { name: string; id: string }) => 
              response.data.relations.course_batches[courseId]?.includes(batch.id)
            );
            setAvailableBatches(batches);
            
            if (batchId && batches.find((b: { id: string }) => b.id === batchId)) {
              setSelectedBatch(batchId);
              const subjects = response.data.subjects.filter((subject: { name: string; id: string }) => 
                response.data.relations.batch_subjects[batchId]?.includes(subject.id)
              );
              setAvailableSubjects(subjects);
              
              const batchProjectIds = response.data.relations.batch_projects?.[batchId] || [];
              const projects = (response.data.projects || []).filter((p: Project) => batchProjectIds.includes(p.batch_project_id));
              setAvailableProjects(projects);
              
              const coordinatorNames = (response.data.relations.coordinators_relations[batchId] || []).map((name: string) => name.trim());
              const coordinators = response.data.coordinators.filter((coordinator: { name: string; id: string }) => 
                coordinatorNames.includes(coordinator.name.trim())
              );
              setAvailableCoordinators(coordinators);
              
              if (coordinatorId && coordinators.find((c: { name: string; id: string }) => c.id === coordinatorId)) {
                setSelectedCoordinator(coordinatorId);
              }
              
              const subjectId = navigationPayload.subject_id ?? previousFilters.subject_id;
              if (subjectId && subjects.find((s: { id: string }) => s.id === subjectId)) {
                setSelectedSubject(subjectId);
              }
              
              const projectId = navigationPayload.project_id ?? previousFilters.project_id;
              const batchProjectId = navigationPayload.batch_project_id ?? previousFilters.batch_project_id;
              if (projectId != null && batchProjectId != null && projects.find((p: Project) => p.id === projectId && p.batch_project_id === batchProjectId)) {
                setSelectedProject(`${projectId}_${batchProjectId}`);
              }
            }
          }
        }
        
        setFiltersLoaded(true);
        setShouldAutoApply(true);
      } catch (err) {
        console.error('Error loading filters:', err);
        setError('Failed to load filter data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    if (!filtersLoaded) return;
    
    if (selectedCollege) {
      const courses = filters.courses.filter(course => 
        filters.relations.college_courses[selectedCollege]?.includes(course.id)
      );
      setAvailableCourses(courses);
      
      if (selectedCourse && !courses.find(c => c.id === selectedCourse)) {
        setSelectedCourse('');
        setSelectedBatch('');
        setSelectedCoordinator('');
        setSelectedSubject('');
      }
    } else {
      setAvailableCourses([]);
      setSelectedCourse('');
      setSelectedBatch('');
      setSelectedCoordinator('');
      setSelectedSubject('');
      setSelectedProject('');
    }
  }, [selectedCollege, filters, filtersLoaded, selectedCourse]);

  useEffect(() => {
    if (!filtersLoaded) return;
    
    if (selectedCourse) {
      const batches = filters.batches.filter(batch => 
        filters.relations.course_batches[selectedCourse]?.includes(batch.id)
      );
      setAvailableBatches(batches);
      
      if (selectedBatch && !batches.find(b => b.id === selectedBatch)) {
        setSelectedBatch('');
        setSelectedCoordinator('');
        setSelectedSubject('');
        setSelectedProject('');
      }
    } else {
      setAvailableBatches([]);
      setSelectedBatch('');
      setSelectedCoordinator('');
      setSelectedSubject('');
      setSelectedProject('');
    }
  }, [selectedCourse, filters, filtersLoaded, selectedBatch]);

  useEffect(() => {
    if (selectedBatch) {
      const subjects = filters.subjects.filter(subject => 
        filters.relations.batch_subjects[selectedBatch]?.includes(subject.id)
      );
      setAvailableSubjects(subjects);
      
      const batchProjectIds = filters.relations.batch_projects?.[selectedBatch] || [];
      const projects = filters.projects.filter(p => batchProjectIds.includes(p.batch_project_id));
      setAvailableProjects(projects);
      setSelectedSubject(prev => (subjects.some(s => s.id === prev) ? prev : ''));
      setSelectedProject(prev => {
        if (!prev) return '';
        const [id, bpId] = prev.split('_').map(Number);
        return projects.some(p => p.id === id && p.batch_project_id === bpId) ? prev : '';
      });
    } else {
      setAvailableSubjects([]);
      setAvailableProjects([]);
      setSelectedSubject('');
      setSelectedProject('');
    }
  }, [selectedBatch, filters]);

  useEffect(() => {
    if (!filtersLoaded) return;
    
    if (selectedBatch) {
      const coordinatorNames = (filters.relations.coordinators_relations[selectedBatch] || []).map(name => name.trim());
      const coordinators = filters.coordinators.filter(coordinator => 
        coordinatorNames.includes(coordinator.name.trim())
      );
      setAvailableCoordinators(coordinators);
      
      const coordinatorId = navigationPayload.coordinator_id || navigationCoordinatorId || previousFilters.coordinator_id || '';
      const isCurrentCoordinatorValid = selectedCoordinator && coordinators.find((c: { name: string; id: string }) => c.id === selectedCoordinator);
      const isNavigationCoordinatorValid = coordinatorId && coordinators.find((c: { name: string; id: string }) => c.id === coordinatorId);
      
      if (!isCurrentCoordinatorValid && isNavigationCoordinatorValid) {
        setSelectedCoordinator(coordinatorId);
      } else if (!isCurrentCoordinatorValid && !isNavigationCoordinatorValid) {
        setSelectedCoordinator('');
      }
    } else {
      setAvailableCoordinators([]);
      setSelectedCoordinator('');
      setSelectedSubject('');
      setSelectedProject('');
    }
  }, [selectedBatch, filters, filtersLoaded]);


  const loadReportData = async () => {
    if (!selectedYear || !selectedCollege || !selectedCourse || !selectedBatch || !selectedCoordinator) {
      const missingFields = [];
      if (!selectedYear) missingFields.push('Year');
      if (!selectedCollege) missingFields.push('College');
      if (!selectedCourse) missingFields.push('Course');
      if (!selectedBatch) missingFields.push('Batch');
      if (!selectedCoordinator) missingFields.push('Coordinator');
      setDataError(`Please select ${missingFields.join(', ')}`);
      setHasAppliedFilters(false);
      return;
    }

    setDataLoading(true);
    setDataError('');
    setHasAppliedFilters(true);
    
    try {
      const payload: Record<string, unknown> = {
        year: parseInt(selectedYear),
        college_id: selectedCollege,
        course_id: selectedCourse,
        batch_id: selectedBatch,
        coordinator_id: selectedCoordinator,
        branch_id: "",
        subject_id: selectedSubject || ""
      };
      if (selectedProject) {
        const [project_id, batch_project_id] = selectedProject.split('_').map(Number);
        payload.project_id = project_id;
        payload.batch_project_id = batch_project_id;
        const selectedProjectObj = availableProjects.find((p) => p.id === project_id && p.batch_project_id === batch_project_id);
        payload.project_name = selectedProjectObj?.name ?? "";
      }

      const response = await authService.post(`coordinator-comparison/all-students-performance-by-coordinator/`, payload);
      setReport(response.data);
      setTotalTimeToBeSpent(response?.data?.total_time_to_be_spent ?? '');
    } catch (err) {
      console.error('Error loading coordinator performance data:', err);
      setDataError('Failed to load data. Please try again.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/coordinator/overall-progress-report');
  };

  const handleDownloadClick = () => {
    const dataToExport =
      selectedStudents.size > 0
        ? sortedStudents.filter((student: StudentData) => selectedStudents.has(student.ID))
        : sortedStudents;

    const visibleColumns = getDisplayColumns().filter((col) => col.visible);
    const firstStudent = dataToExport[0];
    const progressHeaderLabel =
      firstStudent?.progress_percentage_of_should_be_completed_subtasks != null
        ? `Progress (${firstStudent.progress_percentage_of_should_be_completed_subtasks}%)`
        : "Progress %";
    const filteredData = dataToExport.map((student: StudentData) => {
      const filteredStudent: Record<string, unknown> = {};
      visibleColumns.forEach((column) => {
        if (column.key.startsWith('WT_')) {
          filteredStudent[column.label] = getExpandedWTValue(student, column.key);
        } else if (column.key.startsWith('FT_')) {
          filteredStudent[column.label] = getExpandedFTValue(student, column.key);
        } else if (column.key === 'Time_Spent') {
          const noHours = student.Time_Spent ?? '';
          const noOfHoursHeader = `No of hours (${totalTimeToBeSpent || '--'})`;
          filteredStudent[noOfHoursHeader] = noHours || '--';
        } else if (column.key === 'performance_percentage') {
          filteredStudent[progressHeaderLabel] =
            student.performance_percentage != null ? `${student.performance_percentage}%` : '--';
        } else {
          const value = student[column.key as keyof StudentData];
          filteredStudent[column.label] = typeof value === 'string' ? value : String(value ?? '');
        }
      });
      return filteredStudent;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `studentdetails_${currentDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const handleColumnVisibilityChange = (columnKey: string) => {
    const updatedColumns = updateColumnVisibility(PAGE_KEYS.SUB_BATCH_DETAILS, columns, columnKey);
    setColumns(updatedColumns);
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const handleDragStart = (e: React.DragEvent, columnKey: string) => {
    setDraggedColumn(columnKey);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnKey) {
      setDragOverColumn(columnKey);
    }
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumnKey: string) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== targetColumnKey) {
      const draggedIndex = columns.findIndex((col) => col.key === draggedColumn);
      const targetIndex = columns.findIndex((col) => col.key === targetColumnKey);

      const newColumns = [...columns];
      const [draggedItem] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(targetIndex, 0, draggedItem);

      setColumns(newColumns);
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
    setIsDragging(false);
  };

  const handleSelectStudent = (studentId: string) => {
    setSelectedStudents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const currentStudents = sortedStudents;
    if (selectedStudents.size === currentStudents.length && currentStudents.length > 0) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(currentStudents.map((student: StudentData) => student.ID)));
    }
  };

  useEffect(() => {
    setSelectedStudents(new Set());
    // Collapse expanded Score_WT and Score_FT when filters change
    setIsScoreWTExpanded(false);
    setIsScoreFTExpanded(false);
  }, [selectedYear, selectedCollege, selectedCourse, selectedBatch, selectedCoordinator, selectedSubject]);

  // Auto-apply only once at page load if all required data is present
  useEffect(() => {
    if (
      shouldAutoApply &&
      !hasAutoApplied &&
      filtersLoaded && 
      selectedYear && 
      selectedCollege && 
      selectedCourse && 
      selectedBatch && 
      selectedCoordinator &&
      !dataLoading &&
      !loading
    ) {
      handleApplyFilters();
      setHasAutoApplied(true);
      setShouldAutoApply(false);
    }
  }, [shouldAutoApply, filtersLoaded, selectedYear, selectedCollege, selectedCourse, selectedBatch, selectedCoordinator, hasAutoApplied, dataLoading, loading]);

  const generateExpandedWTColumns = (students: StudentData[]): ColumnConfig[] => {
    const subjectWeekMap = new Map<string, Set<string>>();
    students.forEach(student => {
      if (student.Score_wts) {
        Object.keys(student.Score_wts).forEach(subject => {
          if (!subjectWeekMap.has(subject)) {
            subjectWeekMap.set(subject, new Set());
          }
          const weeks = Object.keys(student.Score_wts[subject]);
          weeks.forEach(week => {
            subjectWeekMap.get(subject)!.add(week);
          });
        });
      }
    });
    
    const subjectIdToName = new Map<string, string>();
    filters.subjects.forEach(subject => {
      subjectIdToName.set(subject.id, subject.name);
    });
    
    const expandedColumns: ColumnConfig[] = [];
    const subjects = Array.from(subjectWeekMap.keys());
    
    subjects.forEach(subjectId => {
      const subjectName = subjectIdToName.get(subjectId) || subjectId; // Fallback to ID if name not found
      const weeks = Array.from(subjectWeekMap.get(subjectId)!).sort((a, b) => {
        // Sort weeks numerically (Week 1, Week 2, etc.)
        const numA = parseInt(a.match(/\d+/)?.[0] || '0');
        const numB = parseInt(b.match(/\d+/)?.[0] || '0');
        return numA - numB;
      });
      
      weeks.forEach(week => {
        const columnKey = `WT_${subjectId}_${week}`;
        const columnLabel = `${subjectName} ${week}`;
        expandedColumns.push({
          key: columnKey,
          label: columnLabel,
          visible: true,
          sortable: false
        });
      });
    });
    
    return expandedColumns;
  };

  const generateExpandedFTColumns = (students: StudentData[]): ColumnConfig[] => {
    const subjectAssessmentMap = new Map<string, Set<string>>();
    
    students.forEach(student => {
      if (student.Score_fts) {
        Object.keys(student.Score_fts).forEach(subjectId => {
          if (!subjectAssessmentMap.has(subjectId)) {
            subjectAssessmentMap.set(subjectId, new Set());
          }
          const assessments = Object.keys(student.Score_fts[subjectId]);
          assessments.forEach(assessment => {
            subjectAssessmentMap.get(subjectId)!.add(assessment);
          });
        });
      }
    });
    
    const subjectIdToName = new Map<string, string>();
    filters.subjects.forEach(subject => {
      subjectIdToName.set(subject.id, subject.name);
    });
    
    const expandedColumns: ColumnConfig[] = [];
    const subjects = Array.from(subjectAssessmentMap.keys()).sort();
    
    subjects.forEach(subjectId => {
      const subjectName = subjectIdToName.get(subjectId) || subjectId; // Fallback to ID if name not found
      const assessments = Array.from(subjectAssessmentMap.get(subjectId)!).sort();

      if (assessments.length <= 1) {
        const columnKey = `FT_${subjectId}`;
        const columnLabel = `${subjectName} FT`;
        expandedColumns.push({
          key: columnKey,
          label: columnLabel,
          visible: true,
          sortable: false
        });
        return;
      }

      assessments.forEach((assessment, index) => {
        const encodedAssessment = encodeURIComponent(assessment);
        const columnKey = `FT_${subjectId}:::${encodedAssessment}`;
        const columnLabel = `${subjectName} ${index + 1} FT`;
        expandedColumns.push({
          key: columnKey,
          label: columnLabel,
          visible: true,
          sortable: false
        });
      });
    });
    
    return expandedColumns;
  };

  useEffect(() => {
    if (isScoreWTExpanded && report.students.length > 0 && filters.subjects.length > 0) {
      const newExpandedColumns = generateExpandedWTColumns(report.students);
      setExpandedWTColumns(newExpandedColumns);
    }
  }, [isScoreWTExpanded, report.students, filters.subjects]);

  useEffect(() => {
    if (isScoreFTExpanded && report.students.length > 0 && filters.subjects.length > 0) {
      const newExpandedColumns = generateExpandedFTColumns(report.students);
      setExpandedFTColumns(newExpandedColumns);
    }
  }, [isScoreFTExpanded, report.students, filters.subjects]);

  const handleApplyFilters = () => {
    const currentFilters: Record<string, unknown> = {
      year: selectedYear,
      college_id: selectedCollege,
      course_id: selectedCourse,
      batch_id: selectedBatch,
      coordinator_id: selectedCoordinator,
      branch_id: "",
      subject_id: selectedSubject
    };
    if (selectedProject) {
      const [project_id, batch_project_id] = selectedProject.split('_').map(Number);
      currentFilters.project_id = project_id;
      currentFilters.batch_project_id = batch_project_id;
    }
    sessionStorage.setItem('batchDetailsFilters', JSON.stringify(currentFilters));
    
    loadReportData();
  };

  const handleStudentClick = (studentId: string, courseId: string, batchId: string) => {
    sessionStorage.setItem('currentStudentData', JSON.stringify({
      studentId: studentId,
      courseId: courseId,
      batchId: batchId
    }));
    
    window.open('/coordinator/student-dashboard', '_blank');
  };

  const handleScoreWTClick = () => {
    if (isScoreWTExpanded) {
      // Collapse: just hide expanded columns
      setIsScoreWTExpanded(false);
    } else {
      // Expand: generate columns from current data
      if (report.students.length > 0 && filters.subjects.length > 0) {
        const newExpandedColumns = generateExpandedWTColumns(report.students);
        setExpandedWTColumns(newExpandedColumns);
        setIsScoreWTExpanded(true);
      }
    }
  };

  const handleExpandedWTColumnClick = () => {
    setIsScoreWTExpanded(false);
  };

  const handleScoreFTClick = () => {
    if (isScoreFTExpanded) {
      // Collapse: just hide expanded columns
      setIsScoreFTExpanded(false);
    } else {
      // Expand: generate columns from current data
      if (report.students.length > 0 && filters.subjects.length > 0) {
        const newExpandedColumns = generateExpandedFTColumns(report.students);
        setExpandedFTColumns(newExpandedColumns);
        setIsScoreFTExpanded(true);
      }
    }
  };

  const handleExpandedFTColumnClick = () => {
    // Clicking any expanded column collapses back to total
    setIsScoreFTExpanded(false);
  };

  // Get the columns to display (either normal or with expanded WT/FT columns)
  const getDisplayColumns = (): ColumnConfig[] => {
    let newColumns = [...columns];
    
    // Replace Score_WT with expanded columns if expanded
    if (isScoreWTExpanded && expandedWTColumns.length > 0) {
      const scoreWTIndex = newColumns.findIndex(col => col.key === 'Score_WT');
      if (scoreWTIndex !== -1) {
        newColumns.splice(scoreWTIndex, 1, ...expandedWTColumns);
      }
    }
    
    // Replace Score_FT with expanded columns if expanded
    if (isScoreFTExpanded && expandedFTColumns.length > 0) {
      const scoreFTIndex = newColumns.findIndex(col => col.key === 'Score_FT');
      if (scoreFTIndex !== -1) {
        newColumns.splice(scoreFTIndex, 1, ...expandedFTColumns);
      }
    }
    
    return newColumns;
  };

  // Get value for expanded WT column
  const getExpandedWTValue = (student: StudentData, columnKey: string): string => {
    // columnKey format: WT_subject_week
    const match = columnKey.match(/^WT_(.+?)_(.+)$/);
    if (!match) return '-';
    
    const [, subject, week] = match;
    if (student.Score_wts && student.Score_wts[subject] && student.Score_wts[subject][week]) {
      return student.Score_wts[subject][week];
    }
    return '-';
  };

  // Get value for expanded FT column
  const getExpandedFTValue = (student: StudentData, columnKey: string): string => {
    // columnKey formats: FT_subject OR FT_subject:::encodedAssessmentName
    const multiAssessmentMatch = columnKey.match(/^FT_(.+?):::(.+)$/);
    if (multiAssessmentMatch) {
      const [, subjectId, encodedAssessmentName] = multiAssessmentMatch;
      const assessmentName = decodeURIComponent(encodedAssessmentName);
      if (student.Score_fts?.[subjectId]?.[assessmentName]) {
        return student.Score_fts[subjectId][assessmentName];
      }
      return '-';
    }

    const singleAssessmentMatch = columnKey.match(/^FT_(.+)$/);
    if (!singleAssessmentMatch) return '-';
    
    const [, subjectId] = singleAssessmentMatch;
    if (student.Score_fts && student.Score_fts[subjectId]) {
      const assessments = Object.keys(student.Score_fts[subjectId]).sort();
      if (assessments.length > 0) {
        const firstAssessment = assessments[0];
        return student.Score_fts[subjectId][firstAssessment];
      }
    }
    return '-';
  };

  const isAbsentScore = (value: string): boolean => value.toLowerCase().includes('absent');


  const calculatePercentage = (value: number, total: number): string => {
    if (total === 0) return '0.00';
    return ((value / total) * 100).toFixed(2);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedStudents = () => {
    const students = report.students;
    
    if (!sortField) return students;

    return students.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      switch (sortField) {
        case 'USN':
          aValue = a.USN;
          bValue = b.USN;
          break;
        case 'ID':
          aValue = a.ID;
          bValue = b.ID;
          break;
        case 'Name':
          aValue = a.Name;
          bValue = b.Name;
          break;
        case 'Batch':
          aValue = a.Batch;
          bValue = b.Batch;
          break;
        case 'Progress':
          aValue = a.Progress;
          bValue = b.Progress;
          break;
        case 'Time_Spent':
          aValue = a.Time_Spent;
          bValue = b.Time_Spent;
          break;
        case 'performance_percentage':
          aValue = a.performance_percentage ?? 0;
          bValue = b.performance_percentage ?? 0;
          break;
        case 'Score_WT':
          aValue = a.Score_WT;
          bValue = b.Score_WT;
          break;
        case 'Score_FT':
          aValue = a.Score_FT;
          bValue = b.Score_FT;
          break;
        case 'Grade':
          aValue = a.Grade;
          bValue = b.Grade;
          break;
        default:
          if (sortField.startsWith('WT_')) {
            aValue = getExpandedWTValue(a, sortField);
            bValue = getExpandedWTValue(b, sortField);
            break;
          }
          if (sortField.startsWith('FT_')) {
            aValue = getExpandedFTValue(a, sortField);
            bValue = getExpandedFTValue(b, sortField);
            break;
          }
          return 0;
      }

      if (isScoreSortField(sortField) || sortField.startsWith('WT_') || sortField.startsWith('FT_')) {
        return compareNumericScore(aValue, bValue, sortDirection);
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (isTimelinessSortField(sortField)) {
          return compareTimeliness(aValue, bValue, sortDirection);
        }
        return compareMaybeNumeric(aValue, bValue, sortDirection);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return compareMaybeNumeric(aValue, bValue, sortDirection);
      }

      return 0;
    });
  };

  const sortedStudents = getSortedStudents();

  return (
    <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px" }}>
      <Card>
        <CardContent
          style={{
            height: `calc(100vh - 15px)`,
            overflow: "hidden",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            mb={1}
            style={{ flexShrink: 0 }}
          >
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}
            >
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Year
                </InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedYear(e.target.value)}
                  label="Year"
                  disabled={loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Year
                  </MenuItem>
                  {filters.years.map(year => (
                    <MenuItem
                      key={year}
                      value={year.toString()}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  College
                </InputLabel>
                <Select
                  value={selectedCollege}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedCollege(e.target.value)}
                  label="College"
                  disabled={loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select College
                  </MenuItem>
                  {filters.colleges.map(college => (
                    <MenuItem
                      key={college.id}
                      value={college.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {college.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Course
                </InputLabel>
                <Select
                  value={selectedCourse}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedCourse(e.target.value)}
                  label="Course"
                  disabled={!selectedCollege || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Course
                  </MenuItem>
                  {availableCourses.map(course => (
                    <MenuItem
                      key={course.id}
                      value={course.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {course.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Batch
                </InputLabel>
                <Select
                  value={selectedBatch}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedBatch(e.target.value)}
                  label="Batch"
                  disabled={!selectedCourse || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Batch
                  </MenuItem>
                  {availableBatches.map(batch => (
                    <MenuItem
                      key={batch.id}
                      value={batch.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {batch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Coordinator
                </InputLabel>
                <Select
                  value={selectedCoordinator}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedCoordinator(e.target.value)}
                  label="Coordinator"
                  disabled={!selectedBatch || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Coordinator
                  </MenuItem>
                  {availableCoordinators.map(coordinator => (
                    <MenuItem
                      key={coordinator.id}
                      value={coordinator.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {coordinator.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Subject
                </InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedSubject(e.target.value);
                    if (e.target.value) setSelectedProject('');
                  }}
                  label="Subject"
                  disabled={!selectedBatch || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Subject
                  </MenuItem>
                  {availableSubjects.map(subject => (
                    <MenuItem
                      key={subject.id}
                      value={subject.id}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ alignSelf: "center", fontSize: "14px", px: 0.5 }}>OR</Typography>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 180 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Project
                </InputLabel>
                <Select
                  value={selectedProject}
                  onChange={(e: SelectChangeEvent<string>) => {
                    setSelectedProject(e.target.value);
                    if (e.target.value) setSelectedSubject('');
                  }}
                  label="Project"
                  disabled={!selectedBatch || loading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Project
                  </MenuItem>
                  {availableProjects.map(project => (
                    <MenuItem
                      key={project.batch_project_id}
                      value={`${project.id}_${project.batch_project_id}`}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyFilters}
                disabled={loading || dataLoading}
                style={{
                  height: "36px",
                  fontSize: "12px",
                  textTransform: "none",
                }}
              >
                {dataLoading ? 'Loading...' : 'Apply'}
              </Button>
            </Box>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownloadClick}
                disabled={sortedStudents.length === 0 || !hasAppliedFilters}
                startIcon={<MdDownload />}
                style={{
                  height: "36px",
                  fontSize: "12px",
                  textTransform: "none",
                  border: "1px solid #ccc",
                  color: "#666",
                  whiteSpace: "nowrap",
                }}
              >
                Export{" "}
                {selectedStudents.size > 0 ? `(${selectedStudents.size})` : ""}
              </Button>
              <IconButton
                onClick={handleSettingsClick}
                size="small"
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "6px",
                  height: "36px",
                  width: "36px",
                }}
              >
                <MdSettings size={18} />
              </IconButton>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            style={{ 
              marginTop: "8px", 
              flexGrow: 1, 
              overflow: "auto",
              position: "relative"
            }}  
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRight: "1px solid #e0e0e0",
                      fontWeight: 600,
                      fontSize: "12px",
                      padding: "8px 12px",
                      minWidth: "50px",
                      textAlign: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      pointerEvents: "auto",
                    }}
                  >
                    <Checkbox
                      checked={
                        selectedStudents.size === sortedStudents.length &&
                        sortedStudents.length > 0
                      }
                      indeterminate={
                        selectedStudents.size > 0 &&
                        selectedStudents.size < sortedStudents.length
                      }
                      onChange={handleSelectAll}
                      size="small"
                      style={{
                        pointerEvents: "auto",
                        zIndex: 11,
                        position: "relative",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAll();
                      }}
                    />
                  </TableCell>
                  {getDisplayColumns()
                    .filter((col) => col.visible)
                    .map((column) => {
                      const isExpandedWTColumn = column.key.startsWith('WT_');
                      const isExpandedFTColumn = column.key.startsWith('FT_');
                      const isScoreWTColumn = column.key === 'Score_WT';
                      const isScoreFTColumn = column.key === 'Score_FT';
                      const isClickable = isScoreWTColumn || isExpandedWTColumn || isScoreFTColumn || isExpandedFTColumn;
                      const isExpandedColumn = isExpandedWTColumn || isExpandedFTColumn;
                      
                      return (
                        <TableCell
                          key={column.key}
                          draggable={!isExpandedColumn}
                          onDragStart={(e) => !isExpandedColumn && handleDragStart(e, column.key)}
                          onDragOver={(e) => !isExpandedColumn && handleDragOver(e, column.key)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => !isExpandedColumn && handleDrop(e, column.key)}
                          onDragEnd={handleDragEnd}
                          onClick={() => {
                            if (isClickable) {
                              if (isScoreWTColumn) {
                                handleScoreWTClick();
                              } else if (isExpandedWTColumn) {
                                handleExpandedWTColumnClick();
                              } else if (isScoreFTColumn) {
                                handleScoreFTClick();
                              } else if (isExpandedFTColumn) {
                                handleExpandedFTColumnClick();
                              }
                            } else if (column.sortable) {
                              handleSort(column.key);
                            }
                          }}
                          style={{
                            cursor: isDragging
                              ? "grabbing"
                              : isClickable
                              ? "pointer"
                              : column.sortable
                              ? "pointer"
                              : "grab",
                            whiteSpace: "nowrap",
                            backgroundColor:
                              dragOverColumn === column.key
                                ? "#e3f2fd"
                                : draggedColumn === column.key
                                ? "#f5f5f5"
                                : "#f5f5f5",
                            borderRight: "1px solid #e0e0e0",
                            fontWeight: 600,
                            fontSize: "12px",
                            padding: "8px 12px",
                            minWidth:
                              column.key === "USN"
                                ? "120px"
                                : column.key === "Name"
                                ? "120px"
                                : "100px",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            opacity: draggedColumn === column.key ? 0.5 : 1,
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box display="flex" alignItems="center">
                              {!isExpandedColumn && (
                                <Tooltip title="Drag to reorder">
                                  <span>
                                    <MdDragIndicator
                                      size={16}
                                      style={{
                                        marginRight: "4px",
                                        color: "#666",
                                        cursor: "grab",
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              <span style={{ color: isClickable ? "#1976d2" : "#666" }}>
                                {column.key === "performance_percentage" &&
                                sortedStudents.length > 0
                                  ? `Progress (${
                                      sortedStudents[0]
                                        .progress_percentage_of_should_be_completed_subtasks != null
                                        ? `${sortedStudents[0].progress_percentage_of_should_be_completed_subtasks}%`
                                        : "--"
                                    })`
                                  : column.key === "Time_Spent"
                                  ? `No of hours (${totalTimeToBeSpent || "--"})`
                                  : column.label}
                              </span>
                            </Box>
                            {column.sortable && !isClickable && (
                              <span style={{ marginLeft: "4px" }}>
                                {sortField === column.key ? (
                                  sortDirection === "asc" ? (
                                    <PiSortAscendingLight size={16} />
                                  ) : (
                                    <PiSortDescendingLight size={16} />
                                  )
                                ) : (
                                  <HiOutlineDotsVertical size={16} />
                                )}
                              </span>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading || dataLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={getDisplayColumns().filter((col) => col.visible).length + 1}
                      align="center"
                      style={{ padding: "20px" }}
                    >
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </TableCell>
                  </TableRow>
                ) : !hasAppliedFilters ? (
                  <TableRow>
                    <TableCell
                      colSpan={getDisplayColumns().filter((col) => col.visible).length + 1}
                      align="center"
                      style={{
                        padding: "20px",
                        color: "#666",
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      {error || dataError || "Please select filters and click Apply to view data"}
                    </TableCell>
                  </TableRow>
                ) : sortedStudents.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={getDisplayColumns().filter((col) => col.visible).length + 1}
                      align="center"
                      style={{
                        padding: "20px",
                        color: "#666",
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      No data available for the selected criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedStudents.map((student, index) => (
                    <TableRow
                      key={student.ID}
                      style={{
                        backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <TableCell
                        style={{
                          padding: "6px 12px",
                          textAlign: "center",
                          borderRight: "1px solid #e0e0e0",
                        }}
                      >
                        <Checkbox
                          checked={selectedStudents.has(student.ID)}
                          onChange={() => handleSelectStudent(student.ID)}
                          size="small"
                        />
                      </TableCell>
                      {getDisplayColumns()
                        .filter((col) => col.visible)
                        .map((column) => {
                          const isExpandedWTColumn = column.key.startsWith('WT_');
                          const isExpandedFTColumn = column.key.startsWith('FT_');
                          const expandedWTValue = isExpandedWTColumn ? getExpandedWTValue(student, column.key) : '';
                          const expandedFTValue = isExpandedFTColumn ? getExpandedFTValue(student, column.key) : '';
                          const collapsedScoreValue =
                            column.key === 'Score_WT' || column.key === 'Score_FT'
                              ? String(student[column.key as keyof StudentData] || '')
                              : '';                          
                          return (
                            <TableCell
                              key={column.key}
                              style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                borderRight: "1px solid #e0e0e0",
                                color: "#333",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {isExpandedWTColumn ? (
                                <span className={isAbsentScore(expandedWTValue) ? 'text-danger' : ''}>
                                  {expandedWTValue}
                                </span>
                              ) : isExpandedFTColumn ? (
                                <span className={isAbsentScore(expandedFTValue) ? 'text-danger' : ''}>
                                  {expandedFTValue}
                                </span>
                              ) : column.key === "Time_Spent" ? (
                                student.Time_Spent != null && student.Time_Spent !== "" ? (
                                  student.Time_Spent
                                ) : (
                                  "--"
                                )
                              ) : column.key === "performance_percentage" ? (
                                student.performance_percentage != null
                                  ? `${student.performance_percentage}%`
                                  : "--"
                              ) : column.key === "Score_WT" ||
                                column.key === "Score_FT" ? (
                                <span className={isAbsentScore(collapsedScoreValue) ? 'text-danger' : ''}>
                                  {collapsedScoreValue}
                                </span>
                              ) : column.key === "Progress" ? (
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      String(student[column.key as keyof StudentData] || '').toLowerCase() === "ahead"
                                        ? "#4caf50"
                                        : String(student[column.key as keyof StudentData] || '').toLowerCase() === "laggards"
                                        ? "#f44336"
                                        : "#ff9800",
                                  }}
                                >
                                  {String(student[column.key as keyof StudentData] || '')}
                                </span>
                              ) : column.key === "Name" ? (
                                <span
                                  style={{
                                    color: "#1976d2",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                  }}
                                  onClick={() =>
                                    handleStudentClick(
                                      student.ID,
                                      selectedCourse,
                                      student.batch_id
                                    )
                                  }
                                >
                                  {String(student[column.key as keyof StudentData] || '')}
                                </span>
                              ) : (
                                String(student[column.key as keyof StudentData] || '')
                              )}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Column Settings Popover */}
          <Popover
            open={Boolean(settingsAnchor)}
            anchorEl={settingsAnchor}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Column Visibility
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const resetColumns = resetColumnVisibility(PAGE_KEYS.SUB_BATCH_DETAILS, getDefaultSubBatchDetailsColumns());
                    setColumns(resetColumns);
                  }}
                  sx={{ fontSize: '12px', py: 0.5 }}
                >
                  Reset
                </Button>
              </Box>
              {columns.map((column) => (
                <FormControlLabel
                  key={column.key}
                  control={
                    <Checkbox
                      checked={column.visible}
                      onChange={() => handleColumnVisibilityChange(column.key)}
                    />
                  }
                  label={column.label}
                  sx={{ display: "block", mb: 1 }}
                />
              ))}
            </Box>
          </Popover>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubBatchDetails;

